const ROLE_REASONS = [
    ['REFERENCE', 'REFERENCE_COMPARATOR'],
    ['EFFICIENT', 'ECONOMICALLY_EFFICIENT'],
    ['MINIMUM_COST', 'MINIMUM_TOTAL_RISK_COST'],
    ['MINIMUM_RISK', 'MINIMUM_RESIDUAL_RISK']
]

function emptyResult() {
    return {
        candidates: [],
        anchors: {
            reference: null,
            minimumCost: null,
            minimumRisk: null
        },
        discardedAlternatives: [],
        evidence: {
            efficientAlternatives: [],
            dominatedAlternatives: [],
            ineligibleAlternatives: []
        },
        metadata: {
            evaluatedAlternatives: 0,
            candidateAlternatives: 0,
            discardedAlternatives: 0,
            efficientAlternatives: 0,
            dominatedAlternatives: 0,
            ineligibleAlternatives: 0
        }
    }
}

function isUsableObject(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false
    }

    const prototype = Object.getPrototypeOf(value)
    return prototype === Object.prototype || prototype === null
}

function validId(value) {
    return (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value))
}

function idOf(value) {
    return value !== null && typeof value === 'object' && validId(value.id) ? value.id : null
}

function cloneValue(value, seen = new WeakMap()) {
    if (value === null || typeof value !== 'object') {
        return value
    }

    if (seen.has(value)) {
        return seen.get(value)
    }

    const clone = Array.isArray(value) ? [] : {}
    seen.set(value, clone)
    Object.keys(value).forEach((key) => {
        clone[key] = cloneValue(value[key], seen)
    })
    return clone
}

function sourceFor(alternative) {
    return alternative !== null && typeof alternative === 'object' ? cloneValue(alternative) : {}
}

export function createExecutiveDecisionSet(frontierResult = {}) {
    if (!isUsableObject(frontierResult)) {
        return emptyResult()
    }

    const alternatives = Array.isArray(frontierResult.alternatives) ? frontierResult.alternatives : []
    const referenceId = idOf(frontierResult.referenceAlternative)
    const minimumCostId = idOf(frontierResult.minimumCostAlternative)
    const minimumRiskId = idOf(frontierResult.minimumRiskAlternative)
    const result = emptyResult()
    const candidateIds = new Set()
    const discardedIds = new Set()

    alternatives.forEach((alternative, index) => {
        const source = sourceFor(alternative)
        const id = idOf(alternative)
        const frontier = source.frontier
        const isEligible = frontier?.isEligible === true
        const isEfficient = isEligible && frontier?.isEfficient === true
        const isDominated = isEligible && frontier?.isDominated === true
        const isReference = referenceId !== null && id === referenceId
        const roles = []
        const inclusionReasons = []

        const roleMatches = [
            isReference,
            isEfficient,
            minimumCostId !== null && id === minimumCostId,
            minimumRiskId !== null && id === minimumRiskId
        ]
        roleMatches.forEach((matches, index) => {
            if (matches) {
                roles.push(ROLE_REASONS[index][0])
                inclusionReasons.push(ROLE_REASONS[index][1])
            }
        })

        const isCandidate = isEligible && (isEfficient || isReference)
        const exclusionReasons = !isEligible
            ? ['INVALID_ALTERNATIVE']
            : isDominated && !isReference
            ? ['ECONOMICALLY_DOMINATED']
            : []
        const enriched = {
            ...source,
            executiveDecision: {
                roles: isCandidate ? roles : [],
                isCandidate,
                inclusionReasons: isCandidate ? inclusionReasons : [],
                exclusionReasons: isCandidate ? [] : exclusionReasons
            }
        }

        const identity = id === null ? `index:${index}` : `${typeof id}:${id}`
        if (isCandidate && !candidateIds.has(identity)) {
            candidateIds.add(identity)
            result.candidates.push(enriched)
        } else if (exclusionReasons.length > 0 && !discardedIds.has(identity)) {
            discardedIds.add(identity)
            result.discardedAlternatives.push(enriched)
        }

        if (isEfficient) result.evidence.efficientAlternatives.push(enriched)
        if (isDominated) result.evidence.dominatedAlternatives.push(enriched)
        if (frontier?.isEligible === false) {
            result.evidence.ineligibleAlternatives.push(enriched)
        }

        if (result.anchors.reference === null && isReference) {
            result.anchors.reference = enriched
        }
        if (result.anchors.minimumCost === null && minimumCostId !== null && id === minimumCostId) {
            result.anchors.minimumCost = enriched
        }
        if (result.anchors.minimumRisk === null && minimumRiskId !== null && id === minimumRiskId) {
            result.anchors.minimumRisk = enriched
        }
    })

    result.metadata = {
        evaluatedAlternatives: alternatives.length,
        candidateAlternatives: result.candidates.length,
        discardedAlternatives: result.discardedAlternatives.length,
        efficientAlternatives: result.evidence.efficientAlternatives.length,
        dominatedAlternatives: result.evidence.dominatedAlternatives.length,
        ineligibleAlternatives: result.evidence.ineligibleAlternatives.length
    }

    return result
}
